import type { createBrowserRouter } from 'react-router-dom'

type Router = ReturnType<typeof createBrowserRouter>

const POLL_INTERVAL_MS = 60_000
const VERSION_URL = '/version.json'

let pendingReload = false
let started = false

const isReloadable = (current: string, remote: string): boolean => {
  if (current === 'dev' || remote === 'dev') return false
  if (!current || !remote) return false
  return current !== remote
}

const fetchSha = async (): Promise<string | null> => {
  try {
    const res = await fetch(VERSION_URL, {
      cache: 'no-store',
      credentials: 'same-origin',
    })
    if (!res.ok) return null
    const data = (await res.json()) as { sha?: unknown }
    return typeof data.sha === 'string' ? data.sha : null
  } catch {
    return null
  }
}

const checkOnce = async (currentSha: string): Promise<void> => {
  if (pendingReload) return
  const remote = await fetchSha()
  if (remote && isReloadable(currentSha, remote)) {
    pendingReload = true
  }
}

export const setupVersionCheck = (router: Router, currentSha: string): void => {
  if (started) return
  started = true

  if (currentSha === 'dev') return

  const tick = () => {
    void checkOnce(currentSha)
  }

  tick()
  const intervalId = window.setInterval(tick, POLL_INTERVAL_MS)

  const onVisibility = () => {
    if (document.visibilityState === 'visible') tick()
  }
  document.addEventListener('visibilitychange', onVisibility)

  let lastPath = router.state.location.pathname
  router.subscribe((state) => {
    const nextPath = state.location.pathname
    if (nextPath !== lastPath && pendingReload) {
      window.clearInterval(intervalId)
      document.removeEventListener('visibilitychange', onVisibility)
      window.location.reload()
      return
    }
    lastPath = nextPath
  })
}
